import { useEffect, useMemo, useRef, useState } from 'react';
import type { SectionProfile } from '../engine/types';

/**
 * The cut drawn flat, as NuCAD's Cut View panel draws it: every visible layer's
 * outline in the cutting plane, in millimetres, on a grid, with the cut point
 * at the centre. Click two points to measure between them (each snaps to the
 * nearest outline); the wheel zooms.
 */

const MIN_SPAN = 3;
const MAX_SPAN = 30;
const SNAP_PX = 10;

type Point = { x: number; y: number };

function niceStep(span: number): number {
  const raw = span / 8;
  const power = 10 ** Math.floor(Math.log10(raw));
  for (const m of [1, 2, 5, 10]) if (raw <= m * power) return m * power;
  return 10 * power;
}

export function CutCanvas({ profile }: { profile: SectionProfile | null }) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  const [span, setSpan] = useState(14); // mm across the canvas, like NuCAD's 14 mm cut
  // A measurement belongs to the cut it was taken on: a new cut drops it.
  const [measure, setMeasure] = useState<{ profile: SectionProfile | null; picks: Point[] }>({
    profile: null,
    picks: [],
  });
  const picks = useMemo(
    () => (measure.profile === profile ? measure.picks : []),
    [measure, profile],
  );

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    const g = canvas.getContext('2d');
    if (!g) return;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    const scale = w / span;
    const toPx = (x: number, y: number): [number, number] => [w / 2 + x * scale, h / 2 - y * scale];

    g.fillStyle = '#0f141c';
    g.fillRect(0, 0, w, h);
    // Grid: a line every step, the axes brighter.
    const step = niceStep(span);
    g.lineWidth = 1;
    const halfW = w / 2 / scale;
    const halfH = h / 2 / scale;
    for (let v = -Math.ceil(halfW / step) * step; v <= halfW; v += step) {
      g.strokeStyle = Math.abs(v) < 1e-9 ? 'rgba(255,255,255,0.22)' : 'rgba(255,255,255,0.06)';
      const [px] = toPx(v, 0);
      g.beginPath();
      g.moveTo(px, 0);
      g.lineTo(px, h);
      g.stroke();
    }
    for (let v = -Math.ceil(halfH / step) * step; v <= halfH; v += step) {
      g.strokeStyle = Math.abs(v) < 1e-9 ? 'rgba(255,255,255,0.22)' : 'rgba(255,255,255,0.06)';
      const [, py] = toPx(0, v);
      g.beginPath();
      g.moveTo(0, py);
      g.lineTo(w, py);
      g.stroke();
    }
    // The outlines, layer by layer, in each layer's colour.
    for (const layer of profile?.layers ?? []) {
      g.strokeStyle = layer.colour;
      g.lineWidth = 1.8;
      g.beginPath();
      for (let i = 0; i < layer.count; i++) {
        const o = i * 6;
        const [x1, y1] = toPx(layer.data[o]!, layer.data[o + 1]!);
        const [x2, y2] = toPx(layer.data[o + 2]!, layer.data[o + 3]!);
        g.moveTo(x1, y1);
        g.lineTo(x2, y2);
      }
      g.stroke();
    }
    // The measurement.
    if (picks.length) {
      g.strokeStyle = '#3fb950';
      g.fillStyle = '#3fb950';
      g.lineWidth = 2;
      const pts = picks.map((p) => toPx(p.x, p.y));
      g.beginPath();
      pts.forEach(([x, y], i) => (i ? g.lineTo(x, y) : g.moveTo(x, y)));
      g.stroke();
      for (const [x, y] of pts) {
        g.beginPath();
        g.arc(x, y, 3.5, 0, Math.PI * 2);
        g.fill();
      }
      if (picks.length === 2) {
        const d = Math.hypot(picks[1]!.x - picks[0]!.x, picks[1]!.y - picks[0]!.y);
        const [mx, my] = toPx((picks[0]!.x + picks[1]!.x) / 2, (picks[0]!.y + picks[1]!.y) / 2);
        const text = `${d.toFixed(2)} mm`;
        g.font = '600 12px ui-monospace, monospace';
        const tw = g.measureText(text).width;
        g.fillStyle = 'rgba(15,20,28,0.85)';
        g.fillRect(mx - tw / 2 - 5, my - 22, tw + 10, 18);
        g.fillStyle = '#3fb950';
        g.fillText(text, mx - tw / 2, my - 8);
      }
    }
    // The cut point.
    g.strokeStyle = 'rgba(255,255,255,0.5)';
    g.lineWidth = 1;
    const [cx, cy] = toPx(0, 0);
    g.beginPath();
    g.moveTo(cx - 6, cy);
    g.lineTo(cx + 6, cy);
    g.moveTo(cx, cy - 6);
    g.lineTo(cx, cy + 6);
    g.stroke();
    // Scale.
    g.fillStyle = 'rgba(255,255,255,0.55)';
    g.font = '10px ui-sans-serif, system-ui';
    g.fillText(`grid ${step} mm`, 8, h - 8);
  }, [profile, span, picks]);

  /** The nearest outline end to a canvas point, in mm, within SNAP_PX. */
  const snap = (cx: number, cy: number): Point => {
    const canvas = ref.current!;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    const scale = w / span;
    const mm = { x: (cx - w / 2) / scale, y: (h / 2 - cy) / scale };
    let best: Point = mm;
    let bestD = SNAP_PX / scale;
    for (const layer of profile?.layers ?? []) {
      for (let i = 0; i < layer.count * 2; i++) {
        const x = layer.data[(i >> 1) * 6 + (i & 1) * 2]!;
        const y = layer.data[(i >> 1) * 6 + (i & 1) * 2 + 1]!;
        const d = Math.hypot(x - mm.x, y - mm.y);
        if (d < bestD) {
          bestD = d;
          best = { x, y };
        }
      }
    }
    return best;
  };

  return (
    <canvas
      ref={ref}
      className="h-52 w-full cursor-crosshair rounded-md"
      aria-label="The cut, drawn flat"
      onClick={(event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        const point = snap(event.clientX - rect.left, event.clientY - rect.top);
        setMeasure({ profile, picks: picks.length >= 2 ? [point] : [...picks, point] });
      }}
      onWheel={(event) => {
        setSpan((was) =>
          Math.min(MAX_SPAN, Math.max(MIN_SPAN, was * (event.deltaY > 0 ? 1.15 : 0.87))),
        );
      }}
    />
  );
}
